/**
 * Advanced Geofencing Notification Service
 * Monitors device movements via Redux and generates real-time geofencing notifications
 */

import { 
  calculateDistance, 
  isPointInGeofence, 
  checkGeofenceTransitions, 
  normalizeDeviceCoordinates, 
  isValidCoordinates,
  normalizeGeofence,
  batchProcessGeofencing
} from '../utils/geofencingUtils';

class GeofencingNotificationService {
  constructor() {
    this.previousDeviceStates = new Map(); // Store previous device states
    this.notificationHistory = new Map(); // Prevent duplicate notifications
    this.isMonitoring = false;
    this.monitoringInterval = null;
    this.lastProcessedTimestamp = null;
    
    // Configuration
    this.config = {
      checkInterval: 5000, // Check every 5 seconds
      duplicatePreventionWindow: 30000, // Prevent duplicates for 30 seconds
      maxNotificationHistory: 1000, // Keep last 1000 notifications
      minMovementThreshold: 10, // Minimum movement in meters to trigger check
    };
  }

  /**
   * Start monitoring device movements and geofencing
   * @param {Function} dispatch - Redux dispatch function
   * @param {Object} state - Redux state
   */
  startMonitoring(dispatch, state) {
    if (this.isMonitoring) {
      console.log('🔔 Geofencing monitoring already active');
      return;
    }

    console.log('🚀 Starting advanced geofencing notification monitoring');
    this.isMonitoring = true;
    
    // Start periodic monitoring
    this.monitoringInterval = setInterval(() => {
      this.processDeviceMovements(dispatch, state);
    }, this.config.checkInterval);
    
    // Initial processing
    this.processDeviceMovements(dispatch, state);
  }

  /**
   * Stop monitoring
   */
  stopMonitoring() {
    if (this.monitoringInterval) {
      clearInterval(this.monitoringInterval);
      this.monitoringInterval = null;
    }
    
    this.isMonitoring = false;
    console.log('🛑 Stopped geofencing notification monitoring');
  }

  /**
   * Process device movements and generate notifications
   * @param {Function} dispatch - Redux dispatch function
   * @param {Object} state - Redux state
   */
  processDeviceMovements(dispatch, state) {
    try {
      const { devices, locations, auth } = state;
      
      // Check if user is authorized and has permissions
      if (!auth.isAuthorized || !auth.permissions) {
        return;
      }

      // Get all devices (combine different device types)
      const allDevices = this.getAllDevices(devices);
      
      // Get all geofences (beaches, towers, custom)
      const allGeofences = this.getAllGeofences(locations);
      
      if (allDevices.length === 0 || allGeofences.length === 0) {
        return;
      }

      // Process geofencing for all devices
      const geofencingResults = batchProcessGeofencing(
        allDevices, 
        allGeofences, 
        this.previousDeviceStates
      );

      // Generate notifications for geofence transitions
      this.generateNotifications(geofencingResults, dispatch, auth);

      // Update previous states
      this.updatePreviousStates(geofencingResults);
      
      // Cleanup old data
      this.cleanupOldData();

    } catch (error) {
      console.error('❌ Error processing device movements:', error);
    }
  }

  /**
   * Get all devices from different collections
   * @param {Object} devicesState - Devices Redux state
   * @returns {Array} Combined array of all devices
   */
  getAllDevices(devicesState) {
    const devices = [];
    
    // Add devices from different collections
    const deviceCollections = [
      'allDevices',
      'tritonLocations', 
      'aurusLocations', 
      'seaHorseLocations',
      'allCombinedDevices'
    ];
    
    deviceCollections.forEach(collection => {
      if (devicesState[collection] && Array.isArray(devicesState[collection])) {
        devices.push(...devicesState[collection]);
      }
    });
    
    // Remove duplicates based on device ID
    const uniqueDevices = devices.filter((device, index, self) => 
      index === self.findIndex(d => 
        (d.id || d.device_id || d.name) === (device.id || device.device_id || device.name)
      )
    );
    
    return uniqueDevices;
  }

  /**
   * Get all geofences from different collections
   * @param {Object} locationsState - Locations Redux state
   * @returns {Array} Combined array of all geofences
   */
  getAllGeofences(locationsState) {
    const geofences = [];
    
    // Add beach geofences
    if (locationsState.beachLocations && Array.isArray(locationsState.beachLocations)) {
      locationsState.beachLocations.forEach(beach => {
        const geofence = normalizeGeofence({
          id: beach.id,
          name: beach.name || beach.beach_name,
          type: 'polygon',
          polygon: beach.coordinates || beach.polygon
        });
        if (geofence) geofences.push(geofence);
      });
    }
    
    // Add tower geofences (circular)
    if (locationsState.towerLocations && Array.isArray(locationsState.towerLocations)) {
      locationsState.towerLocations.forEach(tower => {
        const geofence = normalizeGeofence({
          id: tower.id,
          name: tower.name,
          type: 'circle',
          center: tower.coordinates,
          radius: tower.radius || 300
        });
        if (geofence) geofences.push(geofence);
      });
    }
    
    // Add custom geofences
    if (locationsState.customGeofences && Array.isArray(locationsState.customGeofences)) {
      locationsState.customGeofences.forEach(geofence => {
        const normalized = normalizeGeofence(geofence);
        if (normalized) geofences.push(normalized);
      });
    }
    
    return geofences;
  }

  /**
   * Generate notifications for geofence transitions
   * @param {Object} geofencingResults - Results from geofencing processing
   * @param {Function} dispatch - Redux dispatch function
   * @param {Object} auth - Auth state
   */
  generateNotifications(geofencingResults, dispatch, auth) {
    Object.entries(geofencingResults).forEach(([deviceId, result]) => {
      const { device, transitions } = result;
      
      // Generate notifications for geofence entries
      transitions.entered.forEach(geofenceId => {
        this.createNotification({
          type: 'geofence_entry',
          deviceId,
          deviceName: device.name || device.device_id || deviceId,
          geofenceId,
          geofenceName: this.getGeofenceName(geofenceId),
          message: `${device.name || deviceId} entered ${this.getGeofenceName(geofenceId)}`,
          priority: 'medium',
          timestamp: new Date().toISOString()
        }, dispatch, auth);
      });
      
      // Generate notifications for geofence exits
      transitions.exited.forEach(geofenceId => {
        this.createNotification({
          type: 'geofence_exit',
          deviceId,
          deviceName: device.name || device.device_id || deviceId,
          geofenceId,
          geofenceName: this.getGeofenceName(geofenceId),
          message: `${device.name || deviceId} exited ${this.getGeofenceName(geofenceId)}`,
          priority: 'low',
          timestamp: new Date().toISOString()
        }, dispatch, auth);
      });
    });
  }

  /**
   * Create a notification if user has permission
   * @param {Object} notificationData - Notification data
   * @param {Function} dispatch - Redux dispatch function
   * @param {Object} auth - Auth state
   */
  createNotification(notificationData, dispatch, auth) {
    // Check if user has permission for this device type
    if (!this.hasNotificationPermission(notificationData, auth.permissions)) {
      return;
    }
    
    // Check for duplicate notifications
    const notificationKey = `${notificationData.deviceId}_${notificationData.type}_${notificationData.geofenceId}`;
    if (this.isDuplicateNotification(notificationKey)) {
      return;
    }
    
    // Create notification
    const notification = {
      id: `geofence_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      ...notificationData,
      organizationId: auth.organization?.id,
      userId: auth.user?.uid,
      isRead: false,
      source: 'geofencing_service'
    };
    
    // Dispatch to Redux
    dispatch({
      type: 'notifications/addNotification',
      payload: notification
    });
    
    // Add to history to prevent duplicates
    this.notificationHistory.set(notificationKey, Date.now());
    
    console.log('🔔 Generated geofencing notification:', notification);
  }

  /**
   * Check if user has permission for this notification
   * @param {Object} notificationData - Notification data
   * @param {Object} permissions - User permissions
   * @returns {boolean}
   */
  hasNotificationPermission(notificationData, permissions) {
    if (!permissions) return false;
    
    const deviceName = notificationData.deviceName || '';
    const geofenceName = notificationData.geofenceName || '';
    
    // Check vehicle permissions
    if (permissions.viewVehicles === true) {
      return true; // Admin can see all notifications
    } else if (Array.isArray(permissions.viewVehicles) && permissions.viewVehicles.length > 0) {
      const deviceNameLower = deviceName.toLowerCase();
      const hasVehiclePermission = permissions.viewVehicles.some(vehicleType => 
        deviceNameLower.includes(vehicleType.toLowerCase()) ||
        deviceNameLower.includes(vehicleType.replace('_', ' ').toLowerCase())
      );
      if (hasVehiclePermission) return true;
    }
    
    // Check tower permissions
    if (permissions.viewTowers === true) {
      if (geofenceName.toLowerCase().includes('tower') || 
          deviceName.toLowerCase().includes('tower')) {
        return true;
      }
    }
    
    // Check geofence permissions
    if (permissions.viewGeofences === true) {
      return true; // Admin can see all geofence notifications
    } else if (Array.isArray(permissions.viewGeofences) && permissions.viewGeofences.length > 0) {
      const geofenceNameLower = geofenceName.toLowerCase();
      return permissions.viewGeofences.some(geofenceType => 
        geofenceNameLower.includes(geofenceType.toLowerCase())
      );
    }
    
    return false;
  }

  /**
   * Check if notification is a duplicate
   * @param {string} notificationKey - Unique notification key
   * @returns {boolean}
   */
  isDuplicateNotification(notificationKey) {
    const lastSent = this.notificationHistory.get(notificationKey);
    if (!lastSent) return false;
    
    const timeSinceLastSent = Date.now() - lastSent;
    return timeSinceLastSent < this.config.duplicatePreventionWindow;
  }

  /**
   * Get geofence name by ID
   * @param {string} geofenceId - Geofence ID
   * @returns {string} Geofence name
   */
  getGeofenceName(geofenceId) {
    // This could be enhanced to maintain a geofence cache
    return geofenceId || 'Unknown Area';
  }

  /**
   * Update previous device states
   * @param {Object} geofencingResults - Current geofencing results
   */
  updatePreviousStates(geofencingResults) {
    Object.entries(geofencingResults).forEach(([deviceId, result]) => {
      this.previousDeviceStates.set(deviceId, {
        coordinates: result.coordinates,
        timestamp: result.timestamp
      });
    });
  }

  /**
   * Cleanup old data to prevent memory leaks
   */
  cleanupOldData() {
    const now = Date.now();
    const cleanupThreshold = this.config.duplicatePreventionWindow * 2;
    
    // Cleanup notification history
    for (const [key, timestamp] of this.notificationHistory.entries()) {
      if (now - timestamp > cleanupThreshold) {
        this.notificationHistory.delete(key);
      }
    }
    
    // Cleanup device states
    for (const [deviceId, state] of this.previousDeviceStates.entries()) {
      if (now - new Date(state.timestamp).getTime() > cleanupThreshold) {
        this.previousDeviceStates.delete(deviceId);
      }
    }
    
    // Limit notification history size
    if (this.notificationHistory.size > this.config.maxNotificationHistory) {
      const entries = Array.from(this.notificationHistory.entries());
      entries.sort((a, b) => a[1] - b[1]); // Sort by timestamp
      const toDelete = entries.slice(0, entries.length - this.config.maxNotificationHistory);
      toDelete.forEach(([key]) => this.notificationHistory.delete(key));
    }
  }

  /**
   * Get service status and statistics
   * @returns {Object} Service status
   */
  getStatus() {
    return {
      isMonitoring: this.isMonitoring,
      deviceStatesCount: this.previousDeviceStates.size,
      notificationHistoryCount: this.notificationHistory.size,
      config: this.config,
      lastProcessedTimestamp: this.lastProcessedTimestamp
    };
  }

  /**
   * Update configuration
   * @param {Object} newConfig - New configuration
   */
  updateConfig(newConfig) {
    this.config = { ...this.config, ...newConfig };
    console.log('🔧 Updated geofencing notification config:', this.config);
  }
}

// Create singleton instance
const geofencingNotificationService = new GeofencingNotificationService();

export default geofencingNotificationService;
